var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useDebouncedCallback } from '@superfunnel/react-hooks/useDebouncedCallback';
import { Colorful } from '@uiw/react-color';
import { ColorPicker, Plus, Trash, Xmark } from 'iconoir-react';
import * as React from 'react';
import { classNames } from '../utils';
import Button from './Button';
import FieldTitle from './FieldTitle';
import Input from './Input';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Tabs, TabsList, TabsTrigger } from './Tabs';
function hexToHsv(hex) {
    const rgb = hexToRgb(hex);
    if (!rgb)
        return { h: 0, s: 0, v: 0 };
    const r = rgb.r / 255;
    const g = rgb.g / 255;
    const b = rgb.b / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    const v = max;
    const d = max - min;
    const s = max === 0 ? 0 : d / max;
    if (max !== min) {
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
        }
        h /= 6;
    }
    return {
        h: Math.round(h * 360),
        s: Math.round(s * 100),
        v: Math.round(v * 100),
    };
}
function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
        }
        : null;
}
function hexToHsl(hex) {
    const rgb = hexToRgb(hex);
    if (!rgb)
        return null;
    const r = rgb.r / 255;
    const g = rgb.g / 255;
    const b = rgb.b / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
        }
        h /= 6;
    }
    return {
        h: Math.round(h * 360),
        s: Math.round(s * 100),
        l: Math.round(l * 100),
    };
}
export default function ColorPickerField({ title, value, defaultColor, defaultPlaceholder, readOnly, className, debounce, allowDelete = false, colorPalette, variant = 'default', onChange, onDelete, }) {
    const [isOpen, setIsOpen] = React.useState(false);
    const [selectedFormat, setSelectedFormat] = React.useState('HEX');
    const [opacity, setOpacity] = React.useState(100);
    const [busy, setBusy] = React.useState(false);
    const [internalValue, setInternalValue] = React.useState(value || '');
    // Update internal value when external value changes
    React.useEffect(() => {
        setInternalValue(value || '');
    }, [value]);
    const debouncedOnChange = useDebouncedCallback(onChange !== null && onChange !== void 0 ? onChange : (() => { }), debounce ? 100 : 0);
    const colorValue = React.useMemo(() => {
        const hsv = hexToHsv(internalValue);
        return {
            h: hsv.h,
            s: hsv.s,
            v: hsv.v,
            a: opacity / 100,
        };
    }, [internalValue, opacity]);
    const handleColorChange = (color) => {
        setInternalValue(color.hex);
        // Update opacity from the color picker's alpha
        if (color.hsva && typeof color.hsva.a === 'number') {
            setOpacity(Math.round(color.hsva.a * 100));
        }
        debouncedOnChange(color.hex);
    };
    const handleHexInputChange = (e) => {
        if (selectedFormat !== 'HEX')
            return;
        const inputValue = e.target.value.replace(/[^a-fA-F0-9]/g, '').slice(0, 6);
        // Allow partial input while typing
        if (inputValue.length >= 1) {
            const newColor = '#' + inputValue;
            setInternalValue(newColor);
            // Only call onChange if it's a valid 6-character hex
            if (inputValue.length === 6) {
                debouncedOnChange(newColor);
            }
        }
    };
    const handleOpacityChange = (e) => {
        const newOpacity = Math.max(0, Math.min(100, Number(e.target.value)));
        setOpacity(newOpacity);
    };
    function pickFromScreen() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!window.EyeDropper) {
                // eslint-disable-next-line quotes
                alert("Eyedropper isn't supported in this browser. Try Chrome/Edge.");
                return;
            }
            setBusy(true);
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 30000);
            try {
                const eyeDropper = new window.EyeDropper();
                const { sRGBHex } = yield eyeDropper.open({ signal: controller.signal });
                setInternalValue(sRGBHex);
                debouncedOnChange(sRGBHex);
            }
            catch (err) {
                if (err.name !== 'AbortError') {
                    console.error('EyeDropper error:', err);
                }
            }
            finally {
                clearTimeout(timeout);
                setBusy(false);
            }
        });
    }
    const formatColor = (color, format) => {
        const rgb = hexToRgb(color);
        const hsl = hexToHsl(color);
        switch (format) {
            case 'RGB':
                return rgb ? `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})` : color;
            case 'HSL':
                return hsl ? `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)` : color;
            case 'HSB':
                return hsl ? `hsb(${hsl.h}, ${hsl.s}%, ${hsl.l}%)` : color;
            default:
                return color;
        }
    };
    return (_jsxs("div", { children: [title && _jsx(FieldTitle, { className: "mb-2", title: title }), _jsxs(Popover, { open: isOpen, onOpenChange: setIsOpen, children: [variant === 'default' ? (_jsx(PopoverTrigger, { asChild: true, children: value && value !== '' ? (_jsx("button", { className: classNames('relative block h-12 w-12 overflow-hidden rounded-md border-0 shadow-xs ring-1 ring-gray-300 ring-inset focus:ring-2 focus:ring-indigo-600 focus:ring-inset', className), disabled: readOnly, children: _jsx("div", { className: "h-full w-full cursor-pointer", style: { backgroundColor: internalValue || defaultColor } }) })) : (_jsx("div", { className: "text-brand-500 focus-within:ring-brand-500 border-brand-200 bg-brand-25 hover:text-brand-500 flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg border border-dashed focus-within:ring-2 focus-within:ring-offset-2 focus-within:outline-hidden", children: _jsx(Plus, {}) })) })) : (_jsx(PopoverTrigger, { className: "w-full", children: _jsx(Input, { className: "w-full", icon: _jsx("div", { className: '-ms-1 size-6 cursor-pointer rounded-md shadow', style: { backgroundColor: internalValue || defaultColor } }), value: value || defaultPlaceholder || defaultColor || '', readOnly: true }) })), _jsx(PopoverContent, { className: classNames('p-4 pt-1.5', variant === 'field' ? 'w-(--radix-popover-trigger-width)' : 'w-80'), align: "start", side: variant === 'default' ? 'right' : 'bottom', sideOffset: 8, avoidCollisions: true, collisionPadding: 16, children: _jsxs("div", { className: "space-y-2", children: [_jsxs("div", { className: "border-base-200 flex w-full items-center justify-between gap-2 border-b pb-2", children: [_jsx("div", { className: "text-base-900 font-inter text-base leading-8 font-medium", children: "Edit Color" }), _jsxs("div", { className: "flex items-center gap-1", children: [_jsx(Button, { size: 'icon', variant: 'ghost', onClick: pickFromScreen, disabled: busy, title: busy ? 'Sampling…' : 'Pick from screen', className: "h-8 w-8", children: _jsx(ColorPicker, { className: "text-base-900 h-5 w-5" }) }), _jsx(Button, { size: 'icon', variant: 'ghost', onClick: () => setIsOpen(false), className: "h-8 w-8", children: _jsx(Xmark, { className: "text-base-900 h-5 w-5" }) })] })] }), _jsx("div", { className: "rounded-lg", children: _jsx(Colorful, { color: colorValue, onChange: handleColorChange, disableAlpha: false, style: {
                                            width: '100%',
                                        } }) }), colorPalette && colorPalette.length > 0 && (_jsx("div", { className: "space-y-2", children: _jsx("div", { className: "grid grid-cols-8 gap-1", children: colorPalette.map((color) => (_jsx("button", { onClick: () => {
                                                setInternalValue(color);
                                                debouncedOnChange(color);
                                            }, className: classNames('border-lg h-8 w-8 rounded-lg border transition-all hover:scale-110', internalValue.toLowerCase() === color.toLowerCase()
                                                ? 'border-gray-700 shadow-md'
                                                : 'border-gray-200 hover:border-gray-300'), style: { backgroundColor: color }, title: color }, color))) }) })), _jsx(Tabs, { value: selectedFormat, onValueChange: (value) => setSelectedFormat(value), children: _jsx(TabsList, { className: "w-full bg-gray-100", children: ['HEX', 'RGB', 'HSL', 'HSB'].map((format) => (_jsx(TabsTrigger, { value: format, className: "flex-1", children: format }, format))) }) }), _jsxs("div", { className: "flex gap-2", children: [_jsx("div", { className: "flex-1", children: _jsxs("div", { className: "relative", children: [_jsx("span", { className: "absolute top-1/2 left-3 -translate-y-1/2 transform text-sm text-gray-500", children: "#" }), _jsx("input", { value: formatColor(internalValue, selectedFormat).replace('#', ''), onChange: handleHexInputChange, readOnly: selectedFormat !== 'HEX', className: classNames('w-full rounded-lg border border-gray-200 py-2 pr-3 pl-8 font-mono text-sm', 'hover:border-base-300 focus-within:border-base-900! focus-within:ring-base-200 focus-within:ring-2', selectedFormat !== 'HEX' &&
                                                            'cursor-not-allowed bg-gray-50'), placeholder: "9E00FF" })] }) }), _jsx("div", { className: "w-20", children: _jsxs("div", { className: "relative", children: [_jsx("input", { type: "number", min: "0", max: "100", value: opacity, onChange: handleOpacityChange, className: classNames('w-full rounded-lg border border-gray-200 py-2 pr-8 pl-3 text-center text-sm', 'hover:border-base-300 focus-within:border-base-900! focus-within:ring-base-200 focus-within:ring-2', '[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none'), onKeyDown: (e) => {
                                                            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                                                e.preventDefault();
                                                            }
                                                        } }), _jsx("span", { className: "absolute top-1/2 right-3 -translate-y-1/2 transform text-sm text-gray-500", children: "%" })] }) })] }), allowDelete && (_jsxs(Button, { size: 'sm', variant: "outline", className: "w-full border-red-200 text-red-600 hover:border-red-300 hover:bg-red-50", onClick: () => {
                                        if (onDelete) {
                                            onDelete();
                                        }
                                        else {
                                            debouncedOnChange('');
                                        }
                                        setIsOpen(false);
                                    }, children: [_jsx(Trash, { className: "h-4 w-4" }), "Delete color"] }))] }) })] })] }));
}
